/**
 * Adult BMI screening and resting-energy estimates based on standard BMI
 * ranges, Mifflin-St Jeor, and optional Katch-McArdle lean mass.
 */
import {
  ALGO_VERSIONS,
  BMI_HEALTHY_MAX,
  BMI_OVERWEIGHT_MAX,
  BMI_UNDERWEIGHT_MAX,
} from "./constants";
import {
  ENGINE_VERSION,
  type BmiClass,
  type BmiValue,
  type BmrValue,
  type EngineMeta,
  type EngineProfile,
  type EngineResult,
} from "./types";

function roundOne(value: number): number {
  return Math.round(value * 10) / 10;
}

function metabolicSex(gender: string | undefined): "male" | "female" | "unspecified" {
  if (gender === "male" || gender === "female") return gender;
  return "unspecified";
}

function createMeta(
  kind: "bmi" | "bmr",
  now: number,
  confidence: EngineMeta["confidence"],
  explanation: string,
  inputs: EngineMeta["inputs"],
): EngineMeta {
  return {
    kind,
    engineVersion: ENGINE_VERSION,
    algorithmVersion: ALGO_VERSIONS[kind],
    computedAt: now,
    estimateKind: "initial",
    confidence,
    explanation,
    inputs,
  };
}

export function calculateBmi(
  profile: EngineProfile,
  now = Date.now(),
): EngineResult<BmiValue> | null {
  const { weightKg, heightCm } = profile;
  if (weightKg === undefined || heightCm === undefined || heightCm <= 0) return null;

  const heightM = heightCm / 100;
  const rawBmi = weightKg / (heightM * heightM);
  const bmi = roundOne(rawBmi);
  let classification: BmiClass;
  if (rawBmi < BMI_UNDERWEIGHT_MAX) classification = "underweight";
  else if (rawBmi < BMI_HEALTHY_MAX) classification = "healthy";
  else if (rawBmi < BMI_OVERWEIGHT_MAX) classification = "overweight";
  else classification = "obesity";

  return {
    value: {
      bmi,
      classification,
      healthyRangeMinKg: roundOne(BMI_UNDERWEIGHT_MAX * heightM * heightM),
      healthyRangeMaxKg: roundOne(24.9 * heightM * heightM),
    },
    meta: createMeta(
      "bmi",
      now,
      "moderate",
      "BMI is an adult screening measure, not a direct measure of body fat, muscle, or overall health.",
      { weightKg: roundOne(weightKg), heightCm: roundOne(heightCm) },
    ),
  };
}

export function calculateBmr(
  profile: EngineProfile,
  now = Date.now(),
): EngineResult<BmrValue> | null {
  const { weightKg, heightCm, age } = profile;
  if (weightKg === undefined || heightCm === undefined || age === undefined) return null;

  const sex = metabolicSex(profile.gender);
  const sexOffset = sex === "male" ? 5 : sex === "female" ? -161 : -78;
  const bmrKcal = Math.round(10 * weightKg + 6.25 * heightCm - 5 * age + sexOffset);
  const bodyFatPercent = profile.bodyFatPercent;
  const hasPlausibleBodyFat =
    bodyFatPercent !== undefined && bodyFatPercent >= 5 && bodyFatPercent <= 60;
  const value: BmrValue = hasPlausibleBodyFat
    ? {
        bmrKcal,
        equation: "mifflin_st_jeor",
        leanMassKcal: Math.round(370 + 21.6 * weightKg * (1 - bodyFatPercent / 100)),
        leanMassEquation: "katch_mcardle",
      }
    : { bmrKcal, equation: "mifflin_st_jeor" };
  const explanation = hasPlausibleBodyFat
    ? "Mifflin-St Jeor provides the primary adult estimate; the lean-mass comparison is low confidence because consumer body-fat estimates are often imprecise."
    : sex === "unspecified"
      ? "Mifflin-St Jeor estimates resting energy; BiteTrack uses the midpoint of the male and female constants when metabolic sex is unspecified."
      : "Mifflin-St Jeor estimates resting energy from weight, height, age, and metabolic sex.";

  return {
    value,
    meta: createMeta("bmr", now, hasPlausibleBodyFat ? "low" : "moderate", explanation, {
      weightKg: roundOne(weightKg),
      heightCm: roundOne(heightCm),
      age,
      gender: sex,
      bodyFatPercent: hasPlausibleBodyFat ? roundOne(bodyFatPercent) : null,
    }),
  };
}
